<template>
  <BlogLayout
    v-if="categoryName"
    :heading="categoryName"
    :breadcrumbItems="computedBreadcrumbItems"
    :faqHelpfulData="faqSidebarData"
    :showForFaQSidebar="true"
    :showFaqSearchTips="false"
    :showFaqHelpful="true"
    layoutId="faq-blog-layout-detail"
  >
  <div class="lg:col-span-3">
    <SearchNav :fallbackUrl="URL_CONFIG.FAQS" :searchBar="false" layoutId="faq-blog-layout-detail"/>
    <hr class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw]" aria-hidden="true"/>
    <faq-question-and-answer 
      :question="faqQuestion" 
      :answer="faqAnswer" 
      :helpfulText="helpfulText" 
      @vote="handleVoteUpdate"
    />
  </div>
  </BlogLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, watchEffect } from "vue"
import { useRoute } from "vue-router";
import BlogLayout from "~/layouts/blogLayout.vue";
import SearchNav from "~/components/ui/organism/take-five-daily/searchNav.vue";
import FaqQuestionAndAnswer from "~/components/ui/organism/faq/faqQuestionAndAnswer.vue";
import { useFaqDetail } from "~/composables/useFaqs";
import { URL_CONFIG } from "~~/shared/utils/url.const";
import { CATEGORY_DETAILS } from "~~/shared/utils/contentTypes.js";
import type { BreadcrumbItem } from "~~/shared/types/common";

const route = useRoute();

const slug = computed(() => {
  const param = route.params.slug!;
  return Array.isArray(param) ? param[param.length - 1]! : param;
});

const formattedTitle = computed(() =>
  slug.value!.replace(/-s-/g, "'s ").replace(/-/g, " ")
);
const question = computed(() =>
  formattedTitle.value.charAt(0).toUpperCase() + formattedTitle.value.slice(1) + '?'
);
const helpfulText = 'Was this article helpful?'
const pageTitle = computed(() => `${question.value} | Thorne`);

useHead({
  title: pageTitle
})

const {
  faq,
  faqSidebarData,
  error,
  pending,
  refresh
} = useFaqDetail(slug);

const faqQuestion = computed(() => faq.value?.data?.faqQuestion);
const faqAnswer = computed(() => faq.value?.data?.faqAnswer);
const faqCategory = computed(() => faq.value?.data?.faqCategory);
const faqUpvotes = computed(() => faq.value?.data?.faqUpvotes);
const faqDownvotes = computed(() => faq.value?.data?.faqDownvotes);
const entryId = computed(() => faq.value?.id);

watchEffect(() => {
  if (!pending.value && !faq.value) {
    showError({
      statusCode: 404,
      statusMessage: "Page Not Found",
    });
  }
})

const categoryDetails = computed(() =>
  CATEGORY_DETAILS.find(item => item.builderIoKey === faqCategory.value) ?? null
);
const categoryName = computed(() =>
  categoryDetails.value?.categoryName?.replace(/&/g, "and")
);
const categoryUrl = computed(() => categoryDetails.value?.url);

const computedBreadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const label = categoryName.value;
  const items: BreadcrumbItem[] = [
    { label: "Home", href: "/" },
    { label: "FAQs", href: URL_CONFIG.FAQS },
  ];
  if (label) {
    items.push({
      label,
      href: categoryUrl.value
    });
  }
  return items;
});

async function handleVoteUpdate(data: 'yes' | 'no') {
  if (!entryId.value) return;
  const isYes = data === 'yes';
  const newVotes = Number(isYes ? faqUpvotes.value : faqDownvotes.value) + 1;
  const fieldName = isYes ? 'faqUpvotes' : 'faqDownvotes';
  const requestData = {
    data: {
      [fieldName]: newVotes
    }
  };

  const { data: updatedVote, error } = await useFetch(URL_CONFIG.INTERNAL_BUILDER_WRITE_API, {
    method: 'POST',
    body: {
      entryId: entryId.value,
      payload: requestData,
      model: 'faqs'
    }
  })

  if (error.value) {
    console.error('Update failed:', error.value)
  } else {
    console.log('Update success:', updatedVote.value)
  }
}

onMounted(() => {
  refresh();
});
</script>
